import React from "react";
import { InstagramLogoIcon, TiktokLogoIcon, XLogoIcon } from "@phosphor-icons/react";

/**
 * Public display of a profile's social links. Renders nothing when the
 * user hasn't filled any in. Each handle becomes an icon-button that
 * opens the canonical profile URL on the platform.
 *
 * Backend stores the bare handle (no @, no URL). We normalize for
 * display so users typing either "@ava" or "ava" or full URLs all
 * resolve correctly.
 */
function normalizeHandle(raw) {
  if (!raw || typeof raw !== "string") return null;
  let v = raw.trim();
  if (!v) return null;
  // If user pasted a full URL, extract the last path segment.
  if (/^https?:\/\//i.test(v)) {
    try {
      const u = new URL(v);
      const last = u.pathname.split("/").filter(Boolean).pop();
      if (last) v = last;
    } catch {
      /* ignore — fall through */
    }
  }
  v = v.replace(/^@/, "");
  return v || null;
}

const PLATFORMS = [
  {
    key: "instagram",
    label: "Instagram",
    Icon: InstagramLogoIcon,
    url: (handle) => `https://instagram.com/${handle}`,
    classes: "hover:text-pink-600 hover:bg-pink-50",
  },
  {
    key: "tiktok",
    label: "TikTok",
    Icon: TiktokLogoIcon,
    url: (handle) => `https://www.tiktok.com/@${handle}`,
    classes: "hover:text-zinc-900 hover:bg-zinc-100",
  },
  {
    key: "x",
    label: "X",
    Icon: XLogoIcon,
    url: (handle) => `https://x.com/${handle}`,
    classes: "hover:text-zinc-900 hover:bg-zinc-100",
  },
];

export default function SocialLinks({ links, size = "default" }) {
  if (!links) return null;
  const entries = PLATFORMS.map((p) => ({ ...p, handle: normalizeHandle(links[p.key]) })).filter(
    (p) => p.handle
  );
  if (entries.length === 0) return null;

  const dim = size === "lg" ? 18 : 16;

  return (
    <div className="inline-flex items-center gap-1" data-testid="profile-social-links">
      {entries.map(({ key, label, Icon, url, handle, classes }) => (
        <a
          key={key}
          href={url(handle)}
          target="_blank"
          rel="noopener noreferrer"
          className={`h-9 w-9 grid place-items-center rounded-full text-zinc-500 transition-colors ${classes}`}
          title={`${label}: @${handle}`}
          aria-label={`${label}: @${handle}`}
          data-testid={`profile-social-${key}`}
        >
          <Icon size={dim} weight="bold" />
        </a>
      ))}
    </div>
  );
}
